import { FC, useMemo, useState, useEffect } from "react"
import { KTSVG, useDebounce } from "../../../../../_metronic/helpers"

import { filter, cloneDeep, remove } from "lodash"
import { DLMLabelSet } from "../_models"

type Props = {
  title: string    
  labelSets: Array<DLMLabelSet>
  selectedSets: Array<number>
  doClose: Function
  updateSelected: Function
  subTitle?: string
  limit?: number  
}

const DLMLabelSetSelectorModal: FC<Props> = ({title, subTitle, labelSets, selectedSets, doClose, updateSelected, limit = 0}) => {
  
  const [selected, setSelected] = useState<Array<number>>([]);  

  const [last_update, setLastUpdate] = useState('');

  const debouncedUpdate = useDebounce(last_update, 600);

  const { available_sets, available_cnt, selected_sets, selected_cnt } = useMemo(() => {
    
    if (! (labelSets && labelSets.length > 0)) { 
      return { available_sets: [], available_cnt: 0, selected_sets: [], selected_cnt: 0} ;
    }
    
    let available_sets: Array<DLMLabelSet> = filter(cloneDeep(labelSets), (o: any) => (! o?.deleted));

    const selected_sets = remove(available_sets, o => (o?.id && selected.indexOf(o.id) > -1));

    const available_cnt: number = available_sets.length;
    const selected_cnt: number = selected_sets.length;

    return { available_sets, available_cnt, selected_sets, selected_cnt};
    
  }, [labelSets, selected]);

  const addSelected = (e: any) => {
    if (! (e && e.target)) return;
    e.stopPropagation();

    const elem = e.target.closest('.gotSetRow');
    if (! (elem && elem?.dataset && elem.dataset?.id)) return;

    let _selected = cloneDeep(selected);
    if (limit > 0 && _selected.length >= limit) {
      _selected.splice(0, 1, parseInt(`${elem.dataset.id}`));
    } else {
      _selected.push(parseInt(`${elem.dataset.id}`));
    }    
    setSelected(_selected);
    setLastUpdate(Date.now().toString())
  }
  
  const removeSelected = (e: any) => {
    if (! (e && e.target)) return;
    e.stopPropagation();

    const elem = e.target.closest('.linkedSetRow');
    if (! (elem && elem?.dataset)) return;
    
    let _selected = cloneDeep(selected);
    _selected.splice(parseInt(elem.dataset.idx), 1);
    setSelected(_selected);
    setLastUpdate(Date.now().toString())
  }
  
  useEffect(() => {
    if (! debouncedUpdate) return;
    updateSelected(selected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedUpdate]);

  useEffect(() => {
    if (! (selectedSets && selectedSets.length > 0)) {
      return;
    }    
    setSelected(selectedSets);
  }, [selectedSets]);
  
  return (
  <>
  <div
    className='modal fade show d-block'
    id='kt_dlm_labelset_selector_modal'
    role='dialog'
    tabIndex={-1}
    aria-modal='true'
  >
    <div className='modal-dialog modal-dialog-centered mw-450px'>
      <div className='modal-content'>
        <div className='modal-header pt-4 pb-1'>
          <div>
            <h2 className='fw-bolder'>{title}</h2>     
            {subTitle && 
              <span className='text-muted fs-6'>{subTitle}</span>                           
            }                    
          </div> 
          <div
            className='btn btn-icon btn-sm btn-active-icon-primary'
            data-kt-roi-select-multiple-labels-modal-action='close'
            onClick={()=> { doClose()}}
            style={{cursor: 'pointer'}}
          >
            <KTSVG path='/media/icons/duotune/arrows/arr061.svg' className='svg-icon-1' />
          </div>                    
        </div>
        
        <div className='modal-body px-6 py-6'>
          <label className="row fs-7 px-4 fw-bold">{`Linked Sets :${selected_cnt}`}{limit > 0 ? ` / ${limit}` : ''}</label>
          <div className='table-responsive min-h-50px mh-150px'>            
            <table
              id='kt_table_selected_labelsets'
              className='table align-middle table-row-bordered table-row-gray-300 fs-7 gy-2 dataTable no-footer'            
            >
              <thead>
                <tr className='text-start text-muted fw-bolder fs-7 text-uppercase gs-0'>
                  <th className="p-1 min-w-100px">Name</th>
                  <th className="p-1 text-center w-75px">Max No</th>                                        
                  <th className="p-1 text-center w-125px">Hour Rating</th>                        
                  <th className="p-1 w-100px"></th>                        
                </tr>
              </thead>                
              <tbody className='text-gray-800 align-middle fs-7'>                                                                    
                {selected_sets.map( (set, idx) => (
                <tr key={`linkedSet${set.id}`} className="linkedSetRow" data-idx={idx}>
                  <td className="p-1">{set.name}</td>
                  <td className="p-1 text-center">{set.maxNo}</td>
                  <td className="p-1 text-center">{set.hourRating}</td>
                  <td className="p-1">
                    <button type='button' className='btn btn-light-danger btn-sm px-2 py-1' onClick={removeSelected} >
                      <KTSVG path='/media/icons/duotune/arrows/arr089.svg' className='svg-icon-5' />
                      Unlink
                    </button>      
                  </td>
                </tr>
                ))}
              </tbody>
            </table>
          </div>
          {(available_cnt > 0) &&
          <>
          <div className='separator my-3 opacity-75 my-2'></div>
          <label className="row fs-7 px-4 fw-bold">{`Available Sets : ${available_cnt}`}</label>
          <div className='table-responsive min-h-50px mh-150px'>            
            <table
              id='kt_table_available_labelsets'
              className='table align-middle table-row-bordered table-row-gray-300 fs-7 gy-2 dataTable no-footer'            
            >
              <thead>
                <tr className='text-start text-muted fw-bolder fs-7 text-uppercase gs-0'>
                  <th className="p-1 min-w-100px">Name</th>
                  <th className="p-1 text-center w-75px">Max No</th>                                        
                  <th className="p-1 text-center w-125px">Hour Rating</th>                        
                  <th className="p-1 w-100px"></th>                        
                </tr>
              </thead>                
              <tbody className='text-gray-800 align-middle fs-7'>                                                                    
                {available_sets.map( set => (
                <tr key={`gotSet${set.id}`} className="gotSetRow" data-id={set.id}>
                  <td className="p-1">{set.name}</td>
                  <td className="p-1 text-center">{set.maxNo}</td>
                  <td className="p-1 text-center">{set.hourRating}</td>
                  <td className="p-1">
                    <button type='button' className='btn btn-light-primary btn-sm px-2 py-1' onClick={addSelected} >
                        <KTSVG path='/media/icons/duotune/arrows/arr075.svg' className='svg-icon-5' />
                        Link
                    </button>      
                  </td>
                </tr>
                ))}
              </tbody>
            </table>
          </div>
          </>
          }
          <div className='separator my-3 opacity-75 my-2'></div>
          <div className='text-center mt-6'>
            <button
              type='button'
              onClick={()=> { doClose()}}
              className='btn btn-light-primary'              
            >
              Done
            </button>
          </div>
        </div>
      </div>    
    </div>
  </div>    
  </>
  )
}
    
export { DLMLabelSetSelectorModal}